import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const LABELS = { monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday" };

const groupSupersets = (items) => {
  const seen = new Set();
  return items.flatMap((item) => {
    const section = item.section_type === "warmup" ? "warmup" : "main";
    const group = item.is_superset && item.superset_group ? `${section}:${item.superset_group}` : null;
    if (!group) return [item];
    if (seen.has(group)) return [];
    seen.add(group);
    return items.filter((candidate) => {
      const candidateSection = candidate.section_type === "warmup" ? "warmup" : "main";
      return candidate.is_superset && `${candidateSection}:${candidate.superset_group}` === group;
    });
  });
};

const WorkoutScheduleTable = ({ plan, onPlayVideo, onSaveExerciseLog }) => {
  const { t } = useLanguage();
  const today = DAYS[(new Date().getDay() + 6) % 7];
  const firstDay = DAYS.find((day) => (plan?.days?.[day] || []).length) || "monday";
  const [selectedDay, setSelectedDay] = useState((plan?.days?.[today] || []).length ? today : firstDay);
  const [videoModal, setVideoModal] = useState(null);
  const [loadingSlug, setLoadingSlug] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);

  const dayData = useMemo(() => DAYS.reduce((result, day) => ({
    ...result, [day]: groupSupersets([...(plan?.days?.[day] || [])].sort((a, b) => {
      const aOrder = a.section_type === "warmup" ? 0 : 1;
      const bOrder = b.section_type === "warmup" ? 0 : 1;
      return aOrder - bOrder || (a.sort_order || 0) - (b.sort_order || 0);
    })),
  }), {}), [plan]);

  if (!plan) {
    return <div className="hpa-program-empty">
      <span className="hpa-program-empty__icon"><i className="fa-solid fa-dumbbell" /></span>
      <p className="hpa-program-eyebrow">Your next chapter</p><h2>No program assigned yet</h2>
      <p>Your coach will build your personalised weekly training plan after your request is approved.</p>
      <Link to="/program-inquiry" className="hpa-program-primary-btn">Request a program</Link>
    </div>;
  }

  const exercises = dayData[selectedDay] || [];
  const completed = exercises.filter((item) => item.user_log).length;
  const heroImage = exercises.find((item) => item.thumbnail_url)?.thumbnail_url;

  const play = async (video) => {
    if (!video.accessible || loadingSlug) return;
    setLoadingSlug(video.slug);
    await onPlayVideo(video, (embedUrl, title) => setVideoModal({ embedUrl, title }));
    setLoadingSlug(null);
  };
  const getDraft = (video) => drafts[video.assignment_id] || {
    feeling: video.user_log?.feeling || "",
    actual_weight: video.user_log?.actual_weight || "", actual_reps: video.user_log?.actual_reps || "", comments: video.user_log?.comments || "",
  };
  const updateDraft = (video, field, value) => setDrafts((current) => ({
    ...current, [video.assignment_id]: { ...getDraft(video), [field]: value },
  }));
  const saveLog = async (video) => {
    if (!onSaveExerciseLog || !video.assignment_id) return;
    setSavingId(video.assignment_id); setSavedId(null);
    setSaveError(null);
    try {
      const log = await onSaveExerciseLog(video, getDraft(video));
      setDrafts((current) => ({ ...current, [video.assignment_id]: {
        feeling: log?.feeling || "",
        actual_weight: log?.actual_weight || "", actual_reps: log?.actual_reps || "", comments: log?.comments || "",
      } }));
      setSavedId(video.assignment_id);
    } catch {
      setSaveError(video.assignment_id);
    } finally { setSavingId(null); }
  };

  return <div className="hpa-program-dashboard">
    <div className="hpa-program-week" role="tablist" aria-label="Training week">
      {DAYS.map((day) => <button key={day} type="button" role="tab" aria-selected={day === selectedDay}
        className={`hpa-program-day ${day === selectedDay ? "is-active" : ""}`} onClick={() => setSelectedDay(day)}>
        <span>{LABELS[day].slice(0, 1)}</span><strong>{LABELS[day].slice(0, 3)}</strong><small>{dayData[day].length || "–"}</small>
      </button>)}
    </div>

    <section className={`hpa-program-hero ${heroImage ? "has-image" : ""}`}>
      {heroImage && <img src={heroImage} alt="" className="hpa-program-hero__image" />}
      <div className="hpa-program-hero__shade" />
      <div className="hpa-program-hero__content">
        <p className="hpa-program-eyebrow">{LABELS[selectedDay]} · Training day</p><h2>{plan.name}</h2>
        <div className="hpa-program-hero__meta"><span><i className="fa-solid fa-dumbbell" /> {exercises.length} exercises</span>
          <span><i className="fa-regular fa-circle-check" /> {completed} logged</span></div>
        {plan.notes && <p className="hpa-program-hero__notes">{plan.notes}</p>}
      </div>
    </section>

    <section className="hpa-program-session">
      <header className="hpa-program-session__head"><div><p className="hpa-program-eyebrow">Today&apos;s session</p>
        <h3>{LABELS[selectedDay]} workout</h3></div><span className="hpa-program-progress">{completed}/{exercises.length}</span></header>
      {!exercises.length ? <div className="hpa-program-rest"><i className="fa-regular fa-moon" /><h4>Recovery day</h4>
        <p>No exercises are scheduled. Rest, recover, and prepare for your next session.</p></div> :
        <div className="hpa-program-exercises">{exercises.map((video, index) => {
          const locked = !video.accessible; const loading = loadingSlug === video.slug; const draft = getDraft(video);
          const open = expandedLog === video.assignment_id;
          const section = video.section_type === "warmup" ? "warmup" : "main";
          const previous = exercises[index - 1];
          const startsSection = index === 0 || (previous?.section_type === "warmup" ? "warmup" : "main") !== section;
          return <React.Fragment key={video.assignment_id || `${video.slug}-${index}`}>
            {startsSection && <div className={`hpa-exercise-section-title hpa-exercise-section-title--${section}`}>
              <span><i className={`fa-solid ${section === "warmup" ? "fa-fire-flame-curved" : "fa-dumbbell"}`} /></span>
              <div><small>{section === "warmup" ? t("Prepare to perform") : t("Primary training block")}</small>
                <h4>{section === "warmup" ? t("Warm-up") : t("Main Workout")}</h4></div>
              <em>{exercises.filter((item) => (item.section_type === "warmup" ? "warmup" : "main") === section).length}</em>
            </div>}
          <article className={`hpa-exercise-card ${locked ? "is-locked" : ""}`}>
            <button type="button" className="hpa-exercise-card__main" onClick={() => play(video)} disabled={locked || loading}>
              <span className="hpa-exercise-card__number">{String(index + 1).padStart(2, "0")}</span>
              <span className="hpa-exercise-card__thumb">{video.thumbnail_url ? <img src={video.thumbnail_url} alt="" /> : <i className="fa-solid fa-dumbbell" />}
                <i className={`hpa-exercise-card__play fa-solid ${locked ? "fa-lock" : loading ? "fa-spinner fa-spin" : "fa-play"}`} /></span>
              <span className="hpa-exercise-card__copy">{video.is_superset && <small className="hpa-exercise-card__superset">Superset {video.superset_group}</small>}
                <strong>{video.title}</strong><small>{locked ? `Requires ${video.library_name}` : video.library_name}</small>
                <span className="hpa-exercise-card__targets">{video.prescribed_reps && <em>{video.prescribed_reps} reps</em>}
                  {video.prescribed_weight && <em>{video.prescribed_weight}</em>}</span></span>
              <i className="fa-solid fa-chevron-right hpa-exercise-card__arrow" />
            </button>
            {!locked && <><button type="button" className={`hpa-exercise-log-toggle ${video.user_log ? "is-complete" : ""}`}
              onClick={() => setExpandedLog(open ? null : video.assignment_id)}><span><i className="fa-regular fa-pen-to-square" /> {video.user_log ? "Update performance" : "Log performance"}</span>
              <i className={`fa-solid fa-chevron-${open ? "up" : "down"}`} /></button>
              {open && <div className="hpa-exercise-log">{video.notes && <p className="hpa-exercise-note"><i className="fa-regular fa-note-sticky" /> {video.notes}</p>}
                <div className="hpa-exercise-log__fields"><label>Weight<input type="text" placeholder="e.g. 20 kg" value={draft.actual_weight} onChange={(e) => updateDraft(video, "actual_weight", e.target.value)} /></label>
                  <label>Reps<input type="text" placeholder="e.g. 12" value={draft.actual_reps} onChange={(e) => updateDraft(video, "actual_reps", e.target.value)} /></label></div>
                <label>Coach notes<textarea placeholder="How did this exercise feel?" value={draft.comments} onChange={(e) => updateDraft(video, "comments", e.target.value)} /></label>
                <fieldset className="hpa-exercise-feeling">
                  <legend>{t("Exercise feeling")}</legend>
                  <div className="hpa-exercise-feeling__chips">
                    {[["bad", "Bad"], ["good", "Good"], ["very_good", "Very good"], ["excellent", "Excellent"]].map(([value, label]) => (
                      <label key={value} className={draft.feeling === value ? "is-selected" : ""}>
                        <input type="radio" name={`feeling-${video.assignment_id}`} value={value} checked={draft.feeling === value}
                          onChange={() => updateDraft(video, "feeling", value)} />
                        <span>{t(label)}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                {saveError === video.assignment_id && <p role="alert">{t("Could not save your performance. Please try again.")}</p>}
                <button type="button" className="hpa-exercise-log__save" onClick={() => saveLog(video)} disabled={savingId === video.assignment_id}>
                  {savingId === video.assignment_id ? "Saving..." : savedId === video.assignment_id ? "Saved" : "Save performance"}</button></div>}</>}
          </article></React.Fragment>;
        })}</div>}
    </section>

    {!!exercises.length && <div className="hpa-program-actions"><div><strong>{LABELS[selectedDay]}</strong><span>{exercises.length} exercises ready</span></div>
      <button type="button" onClick={() => { const item = exercises.find((exercise) => exercise.accessible); if (item) play(item); }}
        disabled={!exercises.some((exercise) => exercise.accessible)}><i className="fa-solid fa-play" /> Start workout</button></div>}

    {videoModal && <div className="hpa-program-modal" onClick={() => setVideoModal(null)}><div className="hpa-program-modal__dialog" onClick={(event) => event.stopPropagation()}>
      <div className="hpa-program-modal__head"><h4>{videoModal.title}</h4><button type="button" onClick={() => setVideoModal(null)} aria-label="Close video">&times;</button></div>
      <div className="hpa-program-modal__video"><iframe src={videoModal.embedUrl} title={videoModal.title} allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
    </div></div>}
  </div>;
};

export default WorkoutScheduleTable;
